import { useEffect, useState } from 'react';
import { fetchSchedulesOnce, fetchSessionsOnce } from '../../../../../components/psychologist/calendar/data';
import { addMinutesToTime, timeToMinutes } from '../../../../../components/psychologist/calendar/utils';
import { isoToZoneParts } from '../../../../../components/psychologist/calendar/timeZoneHelpers';
import apiCoreAdapter from '../../../../../hooks/apiCoreAdapter';
import { PsychologistFreeScheduleStatus, type Slot } from '../../../../../components/SessionDebug';
import { BA_TIMEZONE } from '../../../../../utils/timezone';

export interface RescheduleSlot {
  date: string;
  dayLabel: string;
  startTime: string;
  endTime: string;
}

const DAYS_ES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const ACTIVE_STATUSES = new Set(['active', 'settled', 'scheduled', 'incomplete']);
const REQUIRED_MINUTES = 240; // 4h

/**
 * Versión liviana e independiente de la grilla semanal del cálculo de "horas
 * disponibles en los próximos 7 días" que alimenta el banner 1 (reprogramación),
 * para poder mostrarlo también en diaria y mensual (WeeklyPsychologistCalendar no
 * se monta en esas vistas). Igual que ese cálculo, suma TANTO el horario fijo
 * recurrente como los overrides puntuales de un día específico ("Día específico"),
 * ya que ambos cuentan para la política de 4h mínimas — ver reschedule7DaysData en
 * WeeklyPsychologistCalendar.tsx. Sin los overrides puntuales, una psicóloga que
 * cubre la ventana solo con horarios de día específico aparecía con 0h y el banner
 * se mostraba de forma incorrecta aunque cumpliera la política.
 */
export function useRescheduleAvailabilityBanner(psychologistId: string | undefined) {
  const [rescheduleMinutes, setRescheduleMinutes] = useState(0);
  const [rescheduleSlotsNextWeek, setRescheduleSlotsNextWeek] = useState<RescheduleSlot[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!psychologistId) return;
    let cancelled = false;
    (async () => {
      try {
        const now = new Date();
        const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7);
        const weekKey = `reschedule-banner-${tomorrow.toISOString().slice(0, 10)}_${psychologistId}`;

        const [template, sessions, availability] = await Promise.all([
          fetchSchedulesOnce(psychologistId),
          fetchSessionsOnce(psychologistId, tomorrow, weekEnd, weekKey, 'general'),
          apiCoreAdapter.psychologistAvailability({
            from: tomorrow.toISOString(),
            to: weekEnd.toISOString(),
            source: 'psi_platform',
          }),
        ]);
        if (cancelled) return;

        // Overrides puntuales de "Día específico": mismo criterio que useCalendarData
        // (status AVAILABLE + reason 'one_time_availability'), agrupados en franjas
        // contiguas por fecha para sumarlos igual que un bloque de horario fijo.
        const oneTimeSlots = availability.filter(
          (s: Slot) => s.status === PsychologistFreeScheduleStatus.AVAILABLE && s.reason === 'one_time_availability',
        );
        const oneTimeByDate = new Map<string, string[]>();
        const seenIso = new Set<string>();
        for (const s of oneTimeSlots) {
          if (seenIso.has(s.slot)) continue;
          seenIso.add(s.slot);
          const { date, time } = isoToZoneParts(s.slot, BA_TIMEZONE);
          const list = oneTimeByDate.get(date) ?? [];
          list.push(time);
          oneTimeByDate.set(date, list);
        }
        const oneTimeRangesByDate = new Map<string, Array<{ startTime: string; endTime: string }>>();
        oneTimeByDate.forEach((times, date) => {
          const sorted = [...times].sort((a, b) => timeToMinutes(a) - timeToMinutes(b));
          const ranges: Array<{ startTime: string; endTime: string }> = [];
          let rangeStart = sorted[0];
          let rangeEnd = addMinutesToTime(sorted[0], 30);
          for (let i = 1; i < sorted.length; i++) {
            if (timeToMinutes(sorted[i]) === timeToMinutes(rangeEnd)) {
              rangeEnd = addMinutesToTime(rangeEnd, 30);
            } else {
              ranges.push({ startTime: rangeStart, endTime: rangeEnd });
              rangeStart = sorted[i];
              rangeEnd = addMinutesToTime(sorted[i], 30);
            }
          }
          ranges.push({ startTime: rangeStart, endTime: rangeEnd });
          oneTimeRangesByDate.set(date, ranges);
        });

        let totalMinutes = 0;
        const slots: RescheduleSlot[] = [];
        const blocksByDate = new Map<string, Array<{ start: number; end: number }>>();

        for (let i = 1; i <= 7; i++) {
          const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
          const appWeekday = (date.getDay() + 6) % 7; // 0=Mon..6=Sun
          const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          const dayLabel = `${DAYS_ES[date.getDay()]} ${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
          const blocks: Array<{ start: number; end: number }> = [];
          for (const t of template) {
            if (!t.active || t.weekday !== appWeekday) continue;
            const start = timeToMinutes(t.startTime);
            let end = timeToMinutes(t.endTime);
            if (end === 0) end = 1440;
            totalMinutes += end - start;
            slots.push({ date: dateStr, dayLabel, startTime: t.startTime, endTime: t.endTime });
            blocks.push({ start, end });
          }
          for (const r of oneTimeRangesByDate.get(dateStr) ?? []) {
            const start = timeToMinutes(r.startTime);
            let end = timeToMinutes(r.endTime);
            if (end === 0) end = 1440;
            totalMinutes += end - start;
            slots.push({ date: dateStr, dayLabel, startTime: r.startTime, endTime: r.endTime });
            blocks.push({ start, end });
          }
          blocksByDate.set(dateStr, blocks);
        }

        let reserved = 0;
        blocksByDate.forEach((blocks, dateStr) => {
          if (blocks.length === 0) return;
          for (const session of sessions) {
            if (session.date !== dateStr || !ACTIVE_STATUSES.has(session.status)) continue;
            const sessionStart = timeToMinutes(session.time);
            if (blocks.some((b: { start: number; end: number }) => sessionStart >= b.start && sessionStart < b.end)) {
              reserved += session.duration ?? 60;
            }
          }
        });

        setRescheduleMinutes(Math.max(0, totalMinutes - reserved));
        setRescheduleSlotsNextWeek(slots);
        setReady(true);
      } catch {
        // Ante error, no mostrar el banner (fail-safe)
      }
    })();
    return () => { cancelled = true; };
  }, [psychologistId]);

  const bannerShouldShow = ready && rescheduleMinutes < REQUIRED_MINUTES;

  return { rescheduleMinutes, rescheduleSlotsNextWeek, bannerShouldShow };
}
